import { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Dashboard-wide "Uncollected Billing" tile.
 *
 * Surfaces the aggregate shipped-but-not-yet-invoiced backlog across every
 * partner + a hot-list of the top 5 by pending amount. Reads the new
 * pending_invoice_* fields on /api/sales/all-partners-summary — zero
 * additional backend endpoint needed.
 *
 * The "Copy Reminder" button places a ready-to-paste WhatsApp/email
 * template on the clipboard, targeted at that specific partner:
 *   Hi <PARTNER>, our records show 19,248 parcels (₹13.11 Cr) shipped but
 *   not yet invoiced. Could you send across the invoice or confirm the
 *   count so we can reconcile? Thanks.
 */
const compactINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};

const UncollectedBillingTile = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/sales/all-partners-summary`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setPartners(d.partners || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const { totalCost, totalCount, top5 } = useMemo(() => {
    const withPending = partners.filter((p) => (p.pending_invoice_count || 0) > 0);
    const total = withPending.reduce(
      (acc, p) => {
        acc.cost += Number(p.pending_invoice_cost || 0);
        acc.count += Number(p.pending_invoice_count || 0);
        return acc;
      },
      { cost: 0, count: 0 }
    );
    const top = [...withPending]
      .sort((a, b) => (b.pending_invoice_cost || 0) - (a.pending_invoice_cost || 0))
      .slice(0, 5);
    return { totalCost: total.cost, totalCount: total.count, top5: top };
  }, [partners]);

  const copyReminder = async (partner) => {
    const cc = partner._id || "Partner";
    const count = (partner.pending_invoice_count || 0).toLocaleString();
    const amt = compactINR(partner.pending_invoice_cost);
    const template =
      `Hi ${cc},\n\n` +
      `Our records show ${count} parcels (${amt}) shipped but not yet invoiced ` +
      `on your ledger with us. Could you send across the invoice or confirm the ` +
      `count so we can reconcile?\n\nThanks.`;
    try {
      await navigator.clipboard.writeText(template);
      toast.success(`Reminder for ${cc} copied to clipboard`);
    } catch {
      toast.error("Could not copy reminder");
    }
  };

  if (loading) {
    return (
      <Card className="mt-3 lg:mt-4" data-testid="uncollected-billing-tile-loading">
        <CardContent className="p-3 lg:p-5 flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading uncollected billing…</span>
        </CardContent>
      </Card>
    );
  }

  if (totalCount === 0) return null;

  return (
    <Card
      className="mt-3 lg:mt-4 border-amber-200 bg-gradient-to-br from-amber-50 to-white"
      data-testid="uncollected-billing-tile"
    >
      <CardContent className="p-3 lg:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] lg:text-xs uppercase tracking-wider text-amber-700 font-semibold">
                Uncollected Billing
              </p>
              <p
                className="text-lg lg:text-2xl font-bold text-slate-900"
                data-testid="uncollected-billing-total-cost"
              >
                {compactINR(totalCost)}
              </p>
              <p className="text-[11px] lg:text-xs text-slate-600" data-testid="uncollected-billing-total-count">
                {totalCount.toLocaleString()} parcels shipped but not yet invoiced
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-slate-500 gap-1"
            onClick={() => navigate("/reports/sales-profit")}
            data-testid="uncollected-billing-view-report"
          >
            <ExternalLink className="w-3 h-3" /> Full report
          </Button>
        </div>

        <div className="space-y-1.5" data-testid="uncollected-billing-top5">
          <p className="text-[10px] lg:text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Top 5 partners by pending amount
          </p>
          {top5.map((p, i) => (
            <div
              key={p._id}
              className="flex items-center justify-between gap-2 rounded-md bg-white/70 border border-amber-100 px-2 py-1.5"
              data-testid={`uncollected-billing-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[10px] font-mono text-slate-400 w-4">{i + 1}.</span>
                <span className="text-xs font-semibold text-slate-800 truncate">{p._id}</span>
                <span className="text-[10px] text-slate-500 flex-shrink-0">
                  {(p.pending_invoice_count || 0).toLocaleString()} parcels
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs font-bold text-amber-800 tabular-nums">
                  {compactINR(p.pending_invoice_cost)}
                </span>
                <button
                  type="button"
                  onClick={() => copyReminder(p)}
                  data-testid={`uncollected-billing-remind-${p._id}`}
                  className="ml-1 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-semibold flex items-center gap-1"
                  title="Copy reminder template for this partner"
                >
                  <Copy className="w-2.5 h-2.5" />
                  Remind
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default UncollectedBillingTile;
